import { SearchHit, SearchHitType, SearchHitTypeConfig, SearchHitTypeConfigs, User } from 'shared';
import { CrowndeskAccessRight, hasAccessRight } from '@crowndesk-core/access-rights/access-rights';

type CrowndeskSearchHitType = SearchHitTypeConfig & {
  /** Only searched when the user has this right, like the menu item of the page. */
  accessRight?: CrowndeskAccessRight;
};

const page =
  (route: string) =>
  (hit: SearchHit): (string | number)[] => [route, hit.entityId];

/**
 * The types the CrownDesk search box finds, in display order. Only types with a CrownDesk page are searched: the API
 * also finds deliveries for a client, but CrownDesk has no page to open one.
 */
export const SEARCH_HIT_TYPES: Partial<Record<SearchHitType, CrowndeskSearchHitType>> = {
  client_order: { label: 'CLIENT_ORDER', icon: 'rc-icon-shopping-cart', route: page('/documents/client_orders'), showsDate: true, accessRight: CrowndeskAccessRight.ORDERS },
  client_invoice: { label: 'INVOICE', icon: 'rc-icon-sales-invoice', route: page('/documents/invoices'), showsDate: true, accessRight: CrowndeskAccessRight.BILLING },
  client_credit_note: { label: 'CREDIT_NOTE', icon: 'rc-icon-credit-note', route: page('/documents/credit-notes'), showsDate: true, accessRight: CrowndeskAccessRight.BILLING },
  client_return: { label: 'RETURN', icon: 'rc-icon-return', route: page('/documents/client_returns'), showsDate: true },
  complaint: { label: 'COMPLAINT', icon: 'rc-icon-message-exclamation', route: page('/documents/complaints'), showsDate: true },
  consumer_quote: { label: 'QUOTATION', icon: 'rc-icon-quote', route: page('/tools/consumer_quotes'), showsDate: true },
  supplier: { label: 'SUPPLIER', icon: 'rc-icon-store', route: page('/products/suppliers') },
  article: { label: 'ARTICLE', icon: 'rc-icon-package', route: page('/products/articles') },
};

/**
 * What this user searches: by default (`types`) the types they can open, narrowed to their preferred types when they
 * have any; "search everywhere" (`allTypes`) every type they can open. The customer view is shown to consumers, so it
 * only finds articles.
 */
export function searchTypesFor(
  user: User | null | undefined,
  customerView: boolean,
): { types: SearchHitType[]; allTypes: SearchHitType[] } {
  const openable = (Object.keys(SEARCH_HIT_TYPES) as SearchHitType[]).filter((type) =>
    hasAccessRight(user, SEARCH_HIT_TYPES[type]!.accessRight),
  );
  if (customerView) {
    const articles = openable.filter((type) => type === 'article');
    return { types: articles, allTypes: articles };
  }
  const preferred = user?.settings?.defaultSearchTypes ?? [];
  const narrowed = openable.filter((type) => preferred.includes(type));
  return { types: narrowed.length ? narrowed : openable, allTypes: openable };
}

/**
 * The stored default search types without the ones the user lost the right for: the api refuses to save settings
 * that still hold them (422). Types CrownDesk does not search are left to the api.
 */
export function availableDefaultSearchTypes(user: User | null | undefined, types: SearchHitType[]): SearchHitType[] {
  return types.filter((type) => hasAccessRight(user, SEARCH_HIT_TYPES[type]?.accessRight));
}

/** The hit types as the shared search box takes them. */
export const CROWNDESK_SEARCH_HIT_TYPES: SearchHitTypeConfigs = SEARCH_HIT_TYPES;
